import { scenePlugins } from '@ff-os/pdp-shared-registry/index';
import { logWarning } from '@ff-os/shared-utils/logging';
import { createAuthorizationHeader } from '@ff-os/shared-utils/private-urls';
import { urls } from '@ff-os/shared-utils/urls';
import { Size } from 'ff-canvas';
import { exportProject } from '../../ExportMonitor';
import { AppThunkApi, PdpState } from '../../store';
import { withAuthorization } from '../authorization/withAuthorization';
import { createAsyncAppThunk } from '../createAsyncAppThunk';
import { deleteLocalDraft, saveLocalDraft } from '../localDrafts';

export type PushProjectResult = {
    snapshotId: string;
};

export const pushProject = createAsyncAppThunk<void, PushProjectResult>(
    'projects/push',
    async (_, thunkApi: AppThunkApi) => {

        await thunkApi.dispatch(saveLocalDraft({ throttle: false })).unwrap();

        const response = await withAuthorization(async (state: PdpState) => {
            return fetch(urls.pushProject, await formatRequest(state));
        }, thunkApi);

        if (response.ok) {
            thunkApi.dispatch(deleteLocalDraft());
            return await response.json() as PushProjectResult;
        } else {
            throw new Error(`Push error: ${urls.pushProject} [${response.status}]`);
        }
    });

const formatRequest = async (state: PdpState) => {

    const formData = new FormData();
    const plugin = scenePlugins.require(state.scene.pluginId);

    const project = await exportProject();

    formData.append('outputsJson', JSON.stringify(plugin.toOutputs(state)));
    formData.append('sceneId', state.scene.id);
    formData.append('projectJson', JSON.stringify(project));
    formData.append('locale', state.global.language);
    formData.append('savedOn', Date.now().toString());
    formData.append('sceneVersion', state.compatibility.sceneVersion.toString());

    try {
        const blob = await renderThumbnail(state, { width: 300, height: 300 });
        formData.append('thumbnail', blob);
    } catch (e) {
        logWarning(e);
    }

    return {
        method: 'POST',
        headers: createAuthorizationHeader(state.auth.jwt!),
        body: formData
    };
};

const renderThumbnail = async (
    state: PdpState,
    targetSize: Size
): Promise<Blob> => {
    const plugin = scenePlugins.require(state.scene.pluginId);
    return plugin.renderThumbnail(state, targetSize);
};